import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FloatingLabel, Form } from "react-bootstrap";
import { Column, Row, Select } from "../../../../components";
import { RootState } from "../../../../shared/store";
import { PolicyLocationReadDto } from "../../../model/PolicyLocationReadDto";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { CustomerPolicyDetailLabels as cstPolicyLocationLabels } from '../../../../shared/util/label';
import { SelectOption } from "../../../../shared/components/input/SelectOption";
import { updateLookupData } from "../../../customer-slice/Customer-lookup-slice";
import * as constant from '../../../../shared/util/constant';
import CustomerPolicyLocationService from "./CustomerPolicyLocationService";
import TextInputWithZipCode from "../../../../shared/components/input/TextInputWithZipCode";
import CustomerDetailService from "../../../customer-details/CustomerDetailService";

export interface ICustomerPolicyLocationForm {
    customerId?: string;
    locationId?: string;
    polId?: string;
    polEventId?: string;
    policyLocation: PolicyLocationReadDto;
    setIsFormSaved?: (isSaved: boolean) => void;
    formValidation?: any;
    tabIndex?: number;
}

const CustomerPolicyLocationForm: React.FunctionComponent<ICustomerPolicyLocationForm> = (props) => {
    const { policyLocation, setIsFormSaved, locationId, polId, polEventId, formValidation: externalFormValidation } = props;
    let tabIndex = props.tabIndex;
    const dispatch = useDispatch();
    const customerDetail = useSelector((state: RootState) => state.customerSlice);
    const policyDetailKey = CommonUtility.getPolicyDetailKey(polId, polEventId);
    const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
    const stateUSLookup = useSelector((state: RootState) => state?.LookupState.stateUSList);
    const [states, setStates] = useState<SelectOption[]>([]);
    const customerPolicyLocationService = new CustomerPolicyLocationService();
    const [locationData, setLocationData] = useState<PolicyLocationReadDto>(policyLocation ?? {} as PolicyLocationReadDto);
    const [formValidation, setFormValidation] = useState<any>(externalFormValidation || {});
    const customerDetailService = new CustomerDetailService();

    useEffect(() => {
        initializeLookupData();
    }, []);

    useEffect(() => {
          if (externalFormValidation) {
      setFormValidation(externalFormValidation);
    }
  }, [externalFormValidation]);

    const initializeLookupData = async () => {
        // Initialize lookup data
        if (stateUSLookup && stateUSLookup.length > 0) {
            setStates([...stateUSLookup]);
        } else {
            let initData: any = null;
            initData = await customerPolicyLocationService.getUSStates();
            if (initData && initData.length > 0) {
                dispatch(updateLookupData({ data: initData, type: constant.SlicerLookUpConstant.US_STATE }));
                setStates(initData);
            } else {
                setStates([]);
            }
        }
    };

    useEffect(() => {
        if (policyDetails && policyDetails.policyLineOfBusinesses) {
            const lob = policyDetails.policyLineOfBusinesses.find((l) => l.polEventId === polEventId);
            if (lob && lob.policyLocations) {
                const location = lob.policyLocations.find((loc) => loc.id === locationId);
                if (location) {
                    setLocationData(location);
                }
            }
        }
    }, [locationId, policyDetails]);

    const handleChangeZipCodeOnBlur = (propName: string, val: string) => {
        let validationErrors = customerDetailService.validationForZipCode(propName, val)
        if (Object.keys(validationErrors).length > 0) {
            setFormValidation({ ...formValidation, ...validationErrors })
        } else {
            const newObject = { ...formValidation };
            delete newObject[propName];
            setFormValidation(newObject);
        }
    };

    const onFieldChange = (propName: string, value: string | number) => {
        const updatedData = {
            ...locationData,
            [propName]: value,
        };
        setLocationData(updatedData);

        // Ensure policyDetails is not undefined before parsing
        if (!policyDetails) {
            console.error("Policy details are undefined.");
            return;
        }

        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails)); // Ensure policyDetails is valid JSON

    if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
      const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map((lob) => {
        if (lob.polEventId === polEventId) {
          const locationExists = lob.policyLocations.some((loc) => loc.id === locationId);

                    if (locationExists) {
                        return {
                            ...lob,
                            policyLocations: lob.policyLocations.map((loc: PolicyLocationReadDto) => {
                                if (loc.id === locationId) {
                                    return {
                                        ...loc,
                                        ...updatedData,
                                    };
                                } else {
                                    return loc;
                                }
                            }),
                        };
                    } else {
                        return {
                            ...lob,
                            policyLocations: [
                                {
                                    id: locationId,
                                    ...updatedData,
                                },
                                ...lob.policyLocations,
                            ],
                        };
                    }
                }
                return lob;
            });

            updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;

            dispatch(
                updateSelectedPolicyDetails({
                    policyId: polId,
                    polEventId: polEventId,
                    policyDetails: updatedPolicyDetails,
                })
            );
        }

        if (setIsFormSaved) {
            setIsFormSaved(false);
        }
    };

    return (
        <div className="main-policy-location-div">
            <Row>
                <Column lg={12} md={12}>
                    <Row className="mb-3">
                        <Column lg={6} md={6}
                        className={formValidation['locationNumber_' + locationId]?.isValid == false ? 'feedback-row' : ''}>
                            <FloatingLabel controlId="locationNumber" label="Location Number">
                                <Form.Control
                                    type="number"
                                    name="locationNumber"
                                    value={locationData.locationNumber || ""}
                                    onChange={(e) => onFieldChange("locationNumber", parseInt(e.target.value))}
                                    placeholder="Location Number"
                                    tabIndex={tabIndex}
                                    isInvalid={formValidation['locationNumber_' + locationId]?.isValid === false}
                                    disabled = {true}
                                     />
                                    {formValidation['locationNumber_' + locationId]?.isValid === false && (
                                      <Form.Control.Feedback type="invalid">
                                        {formValidation['locationNumber_' + locationId]?.warningText}
                                      </Form.Control.Feedback>
                                    )}           
                            </FloatingLabel>
                        </Column>
                        <Column lg={6} md={6}>
                            <FloatingLabel controlId="address1" label="Address Line 1">
                                <Form.Control
                                    type="text"
                                    name="address1"
                                    value={locationData.address1 || ""}
                                    onChange={(e) => onFieldChange("address1", e.target.value)}
                                    placeholder="Address Line 1"
                                    tabIndex={tabIndex}
                                />
                            </FloatingLabel>
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Column lg={6} md={6}>
                            <FloatingLabel controlId="address2" label="Address Line 2">
                                <Form.Control
                                    type="text"
                                    name="address2"
                                    value={locationData.address2 || ""}
                                    onChange={(e) => onFieldChange("address2", e.target.value)}
                                    placeholder="Address Line 2"
                                    tabIndex={tabIndex}
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={6} md={6}>
                            <FloatingLabel controlId="city" label="City">
                                <Form.Control
                                    type="text"
                                    name="city"
                                    value={locationData.city || ""}
                                    onChange={(e) => onFieldChange("city", e.target.value)}
                                    placeholder="City"
                                    tabIndex={tabIndex}
                                />
                            </FloatingLabel>
                        </Column>
                    </Row>
                    <Row className="mb-3">
                        <Column lg={6} md={6}>
                            <Select
                                id="state"
                                controlId="state"
                                label={cstPolicyLocationLabels.STATE}
                                options={states}
                                defaultSelected={locationData.state ?
                                    states.filter(option => option.value === locationData.state) :
                                    []
                                }
                                onChange={(values) => onFieldChange('state', values[0] as string)}
                                className='mb-3'
                                tabIndex={tabIndex}
                            />
                        </Column>
                        <Column lg={6} md={6}>
                            <TextInputWithZipCode
                                name="zipCode"
                                label="Zip Code"
                                controlId="zipCode"
                                placeholder="Zip Code"
                                value={locationData.zipCode ?? ""}
                                onChange={(event) => {
                                    onFieldChange('zipCode', event.currentTarget.value);
                                }}
                                onBlur={(e) => handleChangeZipCodeOnBlur('zipCode', locationData.zipCode)}
                                disabled={false}
                                tabIndex={tabIndex}
                                maxLength={10}
                                type={"text"}
                                isInvalid={formValidation['zipCode']?.isValid == false}
                                invalidWarning={formValidation['zipCode']?.warningText}
                            />
                        </Column>
                    </Row>
                </Column>
            </Row>
        </div>
    );
};

export default CustomerPolicyLocationForm;